/**
 * BetaGate — the "beta access" password screen shown before the app during a private beta.
 *
 * Purpose: keep casual visitors and search engines out of an unfinished tool. It is NOT a security
 * control for client data — the real protection is the account login and encryption behind it.
 *
 * THE GATE IS NOW A SERVER SETTING (owner-directed, 2026-09-10: "refactor the beta password
 * function into a .env variable that we can set on production").
 *
 * It was already an env variable — `REACT_APP_BETA_PASSWORD` — but a BUILD-TIME one, inlined into
 * the bundle by Parcel. Two consequences, both of which bit:
 *
 *   SETTING IT ON PRODUCTION DID NOTHING. The value had to exist on whichever machine ran the
 *   build, so the server's environment was irrelevant.
 *
 *   A REBUILD WITHOUT IT SILENTLY REMOVED THE GATE. Moving to the new server compiled
 *   `const BETA_PASSWORD = String('')`, `isBetaGated()` returned false, and the site was open to
 *   anyone. Nothing errored and nothing was logged — the protection just stopped being there.
 *
 * Now the server decides: `BETA_PASSWORD` in its environment, read per request, switched on or off
 * with a restart and no rebuild. The password is never sent to the browser — the typed candidate
 * goes to the server and comes back yes or no — so it can no longer be read out of the JavaScript.
 *
 * FAILS CLOSED. If the status check cannot reach the API the gate stays shut and offers a retry,
 * because the point of it is keeping people out of un-signed-off financial modelling: an API blip
 * must not be a way in. The cost is that a cold-starting API shows the retry for a few seconds.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Lock, ArrowRight, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { getApiBaseUrl } from '../services/adminApi.ts';

const UNLOCK_KEY = 'awiseBetaUnlocked';

type GateState = 'checking' | 'unreachable' | 'open' | 'locked';

function alreadyUnlocked() {
  try {
    return localStorage.getItem(UNLOCK_KEY) === '1';
  } catch {
    return false;
  }
}

/** Ask the server whether this deployment is gated. Never receives the password itself. */
async function fetchGateRequired(): Promise<boolean> {
  const res = await fetch(`${getApiBaseUrl()}/rest/beta/status`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const body = (await res.json()) as { required?: boolean };
  return Boolean(body?.required);
}

/** Ask the server to check a candidate password. */
async function checkPassword(password: string): Promise<boolean> {
  const res = await fetch(`${getApiBaseUrl()}/rest/beta/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const body = (await res.json()) as { ok?: boolean };
  return Boolean(body?.ok);
}

interface BetaGateProps {
  children: React.ReactNode;
}

const BetaGate = ({ children }: BetaGateProps) => {
  const [state, setState] = useState<GateState>(() => (alreadyUnlocked() ? 'open' : 'checking'));
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(async () => {
    setState('checking');
    try {
      setState((await fetchGateRequired()) ? 'locked' : 'open');
    } catch {
      // Fails CLOSED — see the file comment. An unreachable API is not a way past the gate.
      setState('unreachable');
    }
  }, []);

  useEffect(() => {
    if (alreadyUnlocked()) return;
    void check();
  }, [check]);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (await checkPassword(entry.trim())) {
        try { localStorage.setItem(UNLOCK_KEY, '1'); } catch { /* private mode: gate re-shows, fine */ }
        setState('open');
      } else {
        setError('That password wasn’t recognised. Please check and try again.');
      }
    } catch {
      setError('We couldn’t reach the server to check that. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'open') return <>{children}</>;

  const Shell = ({ children: inner }: { children: React.ReactNode }) => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-6 pt-7 pb-8 text-center text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold">
            <span>AgedCare</span><span className="text-white/90">Wise</span>
          </h1>
          <p className="mt-1 text-sm text-white/85">Private beta — access by invitation</p>
        </div>
        {inner}
      </div>
    </div>
  );

  /* Waiting on the server. Said plainly rather than shown as an empty screen. */
  if (state === 'checking') {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 p-8 text-sm text-gray-600">
          <Loader2 size={16} className="animate-spin text-blue-600" />
          Checking access…
        </div>
      </Shell>
    );
  }

  /* The API could not be reached. The gate stays shut, and says why. */
  if (state === 'unreachable') {
    return (
      <Shell>
        <div className="space-y-4 p-6">
          <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-sm text-slate-700">
              We couldn’t reach the server to check access. If the site has been idle it may be
              starting up — this usually clears in under a minute.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void check()}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-2.5 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-xl hover:brightness-[1.04] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <RefreshCw size={16} /> Try again
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="p-6 space-y-4">
        <p className="text-sm text-gray-600">
          Enter the beta access password you were given to continue.
        </p>
        {error && (
          <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <div>
          <label htmlFor="beta-pw" className="block text-sm font-medium text-gray-700 mb-1.5">Access password</label>
          <input
            id="beta-pw"
            type="password"
            autoFocus
            value={entry}
            onChange={(e) => { setEntry(e.target.value); setError(null); }}
            className="w-full rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-2.5 text-gray-900 placeholder-gray-400 shadow-sm transition focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/40 focus:outline-none"
            placeholder="Beta password"
          />
        </div>
        <button
          type="submit"
          disabled={!entry.trim() || submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-2.5 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-xl hover:brightness-[1.04] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Checking…</> : <>Enter <ArrowRight size={16} /></>}
        </button>
        <p className="text-center text-xs text-gray-400">
          AgedCareWise is in private beta. It’s a demonstration and not personal financial advice.
        </p>
      </form>
    </Shell>
  );
};

export default BetaGate;
